import { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useAppStore } from '../stores/appStore';
import { useStoreItems, usePurchaseItem } from '../hooks/useGame';
import type { StoreItem } from '../services/api';
import './StoreModal.css';

interface StoreModalProps {
    isOpen: boolean;
    onClose: () => void;
}

function StoreItemCard({ item, onBuy, isPurchasing }: {
    item: StoreItem;
    onBuy: (itemType: string) => void;
    isPurchasing: boolean;
}) {
    const user = useAppStore(state => state.user);
    const coins = user?.coins ?? 0;
    const canAfford = coins >= item.price;

    return (
        <motion.div
            className="store-item-card"
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ type: 'spring', stiffness: 350, damping: 28 }}
        >
            <div className="store-item-icon">{item.icon}</div>
            <div className="store-item-body">
                <div className="store-item-header">
                    <span className="store-item-name">{item.name}</span>
                    <span className="store-item-mode-badge">{item.mode}</span>
                </div>
                <p className="store-item-description">{item.description}</p>
                <div className="store-item-footer">
                    <div className="store-item-inventory">
                        <span className="inventory-label">In bag:</span>
                        <span className="inventory-count">{item.quantity}</span>
                    </div>
                    <button
                        className={`store-buy-btn ${!canAfford ? 'cannot-afford' : ''}`}
                        onClick={() => onBuy(item.id)}
                        disabled={isPurchasing || !canAfford}
                    >
                        {isPurchasing ? (
                            <span className="store-spinner" />
                        ) : (
                            <>
                                <span className="store-coin-icon">🪙</span>
                                <span>{item.price}</span>
                            </>
                        )}
                    </button>
                </div>
            </div>
        </motion.div>
    );
}

export default function StoreModal({ isOpen, onClose }: StoreModalProps) {
    const user = useAppStore(state => state.user);
    const { data, isLoading } = useStoreItems();
    const { mutate: purchase, isPending } = usePurchaseItem();
    const [purchasingItem, setPurchasingItem] = useState<string | null>(null);
    const [feedback, setFeedback] = useState<{ type: 'success' | 'error'; text: string } | null>(null);

    const handleBuy = (itemType: string) => {
        setPurchasingItem(itemType);
        setFeedback(null);
        purchase(itemType, {
            onSuccess: (data) => {
                if (data.success) {
                    setFeedback({ type: 'success', text: data.message || 'Purchased!' });
                } else {
                    setFeedback({ type: 'error', text: data.message || 'Purchase failed.' });
                }
            },
            onError: (err) => {
                setFeedback({ type: 'error', text: err instanceof Error ? err.message : 'Purchase failed.' });
            },
            onSettled: () => setPurchasingItem(null),
        });
    };

    return (
        <AnimatePresence>
            {isOpen && (
                <div className="store-modal-overlay" onClick={onClose}>
                    <motion.div
                        className="store-modal"
                        initial={{ opacity: 0, scale: 0.95, y: 12 }}
                        animate={{ opacity: 1, scale: 1, y: 0 }}
                        exit={{ opacity: 0, scale: 0.95, y: 12 }}
                        transition={{ type: 'spring', stiffness: 380, damping: 30 }}
                        onClick={e => e.stopPropagation()}
                    >
                        <div className="store-modal-header">
                            <div className="store-title-group">
                                <span className="store-title-icon">🏪</span>
                                <h2 className="store-title">Store</h2>
                            </div>
                            <div className="store-balance">
                                <span className="store-balance-value">{user?.coins ?? 0}</span>
                                <span className="store-balance-icon">🪙</span>
                            </div>
                            <button className="store-close-btn" onClick={onClose}>×</button>
                        </div>

                        <AnimatePresence mode="wait">
                            {feedback && (
                                <motion.div
                                    className={`store-feedback ${feedback.type}`}
                                    initial={{ opacity: 0, y: -6 }}
                                    animate={{ opacity: 1, y: 0 }}
                                    exit={{ opacity: 0 }}
                                >
                                    {feedback.type === 'success' ? '✅' : '❌'} {feedback.text}
                                </motion.div>
                            )}
                        </AnimatePresence>

                        <div className="store-modal-body">
                            {isLoading ? (
                                <div className="store-loading">
                                    <div className="store-loading-spinner" />
                                    <span>Loading items...</span>
                                </div>
                            ) : (
                                <div className="store-items-list">
                                    {data?.items.map(item => (
                                        <StoreItemCard
                                            key={item.id}
                                            item={item}
                                            onBuy={handleBuy}
                                            isPurchasing={isPending && purchasingItem === item.id}
                                        />
                                    ))}
                                </div>
                            )}
                        </div>

                        <p className="store-footer-note">
                            Items are added to your inventory and can be used in any future puzzle.
                        </p>
                    </motion.div>
                </div>
            )}
        </AnimatePresence>
    );
}
